import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import NLLogo from "../pngs/NL_Logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import ContactButton from "./ContactButton";
import { primaryButtonBase } from "../styles/buttonStyles";
import { HeroSubtext } from "./Typography";
import { typography, colors } from "../styles/designSystem";

// ===== Config =====
// Kept narrow on purpose: a CRT has one phosphor plus a couple of alert hues.
// Anything wider and the rotating headline word stops reading as a terminal.
export const HERO_COLORS = [
    colors.primary[500],
    colors.emerald[500],
    colors.amber[400],
    colors.cyan[300],
];

export const HERO_WORDS = ["Products", "Systems", "Interfaces", "Platforms", "Services"];

export const getHeroColor = () => HERO_COLORS[Math.floor(Math.random() * HERO_COLORS.length)];

// Static rather than generated so the column is stable across re-renders.
const STREAM_GLYPHS =
    "7F A2 00 3C D9 41 8B 15 E0 6A 2D FF 91 04 C7 38 5E B1 20 9A 46 D3 0F 77 " +
    "1C E8 53 AA 32 6F B8 09 D4 71 25 9E 4C F0 63 18 8D 2A B6 57 E1 3F 04 CB";

// ===== Animations =====
const typeIn = keyframes`
    from { opacity: 0; transform: translateY(2px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const riseIn = keyframes`
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const streamScroll = keyframes`
    from { transform: translateY(0); }
    to   { transform: translateY(-50%); }
`;

// ===== Layout =====
const ContentLayout = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 480px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    overflow: hidden;
    padding: 3rem 2rem;

    @media (max-width: 768px) {
        padding: 2.5rem 1.25rem;
    }
`;

// Two columns of drifting hex on the outer edges — the terminal's memory dump,
// running quietly behind the record. Hidden on narrow screens where there's no room.
const Stream = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 5.5rem;
    overflow: hidden;
    pointer-events: none;
    opacity: 0.16;
    font-family: ${typography.fontFamily.primary};
    font-size: 0.7rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.primary};
    word-break: break-all;
    text-align: left;
    left: ${({ side }) => (side === "left" ? "2rem" : "auto")};
    right: ${({ side }) => (side === "right" ? "2rem" : "auto")};

    span {
        display: block;
        ${css`animation: ${streamScroll} 26s linear infinite;`}
    }

    @media (max-width: 1200px) {
        display: none;
    }

    @media (prefers-reduced-motion: reduce) {
        span {
            animation: none;
        }
    }
`;

// The terminal frame — corner ticks drawn around the whole record.
const Frame = styled.div`
    position: absolute;
    inset: 2.5rem;
    pointer-events: none;
    border: 1px solid ${({ theme }) => theme.borderLight};

    &::before,
    &::after {
        content: "";
        position: absolute;
        width: 16px;
        height: 16px;
        border: 2px solid ${({ theme }) => theme.primary};
        opacity: 0.65;
    }

    &::before {
        top: -2px;
        left: -2px;
        border-right: 0;
        border-bottom: 0;
    }

    &::after {
        bottom: -2px;
        right: -2px;
        border-left: 0;
        border-top: 0;
    }

    @media (max-width: 768px) {
        inset: 1rem;
    }
`;

const ContentWrapper = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 820px;
    width: 100%;
`;

// ===== Record =====
const Record = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    ${css`animation: ${riseIn} 0.5s ease-out both;`}
`;

const Logo = styled.img`
    width: 88px;
    height: 88px;
    object-fit: contain;
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.borderMedium};
    /* Burn the mark into the active phosphor instead of leaving it full-color. */
    filter: ${({ theme }) => theme.logoFilter};
    box-shadow: 0 0 18px ${({ theme }) => theme.glowSoft};
    margin-bottom: 1.25rem;
`;

const Name = styled.h2`
    font-family: ${typography.fontFamily.display};
    font-size: 2.25rem;
    font-weight: ${typography.fontWeight.bold};
    color: ${({ theme }) => theme.text};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    text-shadow: 0 0 12px ${({ theme }) => theme.glow};
    margin: 0 0 0.5rem;

    @media (max-width: 768px) {
        font-size: 1.5rem;
        letter-spacing: 0.14em;
    }
`;

const RoleLine = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ theme }) => theme.primary};
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        font-size: 0.68rem;
        letter-spacing: 0.12em;
    }
`;

const Headline = styled.h1`
    font-family: ${typography.fontFamily.display};
    font-size: 3.25rem;
    font-weight: ${typography.fontWeight.bold};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    max-width: 100%;
    line-height: 1.25;
    margin: 0 0 1.5rem;
    text-align: center;
    color: ${({ theme }) => theme.text};
    ${css`animation: ${riseIn} 0.5s ease-out 0.1s both;`}

    @media (max-width: 1024px) {
        font-size: 2.5rem;
    }

    @media (max-width: 768px) {
        font-size: 1.75rem;
        letter-spacing: 0.04em;
    }

    .line {
        display: inline;

        @media (max-width: 640px) {
            display: block;
        }
    }
`;

const AnimatedWordWrapper = styled.span`
    display: inline-block;
    transition: width 0.2s ease-out;
    overflow: hidden;
    vertical-align: bottom;
`;

const AnimatedWord = styled.span`
    display: inline-block;
    white-space: nowrap;
    color: ${({ color }) => color};
    text-shadow: 0 0 14px ${({ color }) => color};
    ${css`animation: ${typeIn} 0.2s steps(3, end);`}
`;

const Subtext = styled(HeroSubtext)`
    font-size: 1rem;
    line-height: 1.8;
    max-width: 660px;
    margin: 0 auto 2rem;
    text-align: center;
    color: ${({ theme }) => theme.subtleText};
    ${css`animation: ${riseIn} 0.5s ease-out 0.25s both;`}

    @media (max-width: 768px) {
        font-size: 0.9rem;
        line-height: 1.7;
    }
`;

const Links = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
    ${css`animation: ${riseIn} 0.5s ease-out 0.4s both;`}
`;

const Button = styled.a`
    ${primaryButtonBase}
    padding: 0.85rem 1.5rem;
    font-size: 0.85rem;
`;

// ===== Presentational hero =====
export const HeroContent = ({
    index = 0,
    color = HERO_COLORS[0],
    resumeColor = HERO_COLORS[0],
    linkedinColor = HERO_COLORS[1],
    contactColor = HERO_COLORS[2],
}) => {
    const [wordWidth, setWordWidth] = useState(0);
    const wordRef = useRef(null);

    useEffect(() => {
        if (wordRef.current) {
            setWordWidth(wordRef.current.offsetWidth);
        }
    }, [index]);

    return (
        <ContentLayout>
            <Frame aria-hidden="true" />
            <Stream side="left" aria-hidden="true">
                <span>{STREAM_GLYPHS}{STREAM_GLYPHS}</span>
            </Stream>
            <Stream side="right" aria-hidden="true">
                <span>{STREAM_GLYPHS}{STREAM_GLYPHS}</span>
            </Stream>

            <ContentWrapper>
                <Record>
                    <Logo src={NLLogo} alt="Nicholas Lovett logo" />
                    <Name>Nicholas Lovett</Name>
                    <RoleLine>Senior Software Engineer &#47;&#47; AI &amp; Mobile</RoleLine>

                    <Headline>
                        <span className="line">
                            I Build{" "}
                            <AnimatedWordWrapper style={{ width: wordWidth || undefined }}>
                                <AnimatedWord key={index} ref={wordRef} color={color}>
                                    {HERO_WORDS[index]}
                                </AnimatedWord>
                            </AnimatedWordWrapper>
                            .
                            <br />
                        </span>
                        <span className="line">Then I Ship Them.</span>
                    </Headline>

                    <Subtext>
                        I build and ship production software end to end &mdash; React and React Native
                        clients, Node and Next.js services, and the component architecture underneath
                        them. Ten years inside enterprise engineering teams and as a solo founder, across
                        AT&amp;T, Spectrum, Verizon, and Caterpillar. I design too; it makes the
                        engineering better.
                    </Subtext>

                    <Links>
                        <Button
                            href="/Resume_FS.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            color={resumeColor}
                        >
                            <FontAwesomeIcon icon={faFileAlt} />
                            Resume
                        </Button>

                        <Button
                            href="https://linkedin.com/in/nicholaslovett"
                            target="_blank"
                            rel="noopener noreferrer"
                            color={linkedinColor}
                        >
                            <FontAwesomeIcon icon={faLinkedin} />
                            LinkedIn
                        </Button>

                        <ContactButton color={contactColor} />
                    </Links>
                </Record>
            </ContentWrapper>
        </ContentLayout>
    );
};

// ===== Hook driving the rotating headline word =====
export const useHeroState = () => {
    const [index, setIndex] = useState(0);
    const [color, setColor] = useState(HERO_COLORS[0]);
    const [resumeColor] = useState(getHeroColor);
    const [linkedinColor] = useState(getHeroColor);
    const [contactColor] = useState(getHeroColor);

    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((prev) => (prev + 1) % HERO_WORDS.length);
            setColor(getHeroColor());
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return { index, color, resumeColor, linkedinColor, contactColor };
};

// ===== Page-level hero =====
const StandaloneWrapper = styled.section`
    position: relative;
    width: 100%;
    /* Matches the padding Layout reserves for the fixed banner + nav. */
    min-height: calc(100vh - var(--chrome-h, 145px));
`;

const Hero = () => {
    const heroState = useHeroState();
    return (
        <StandaloneWrapper id="Hero">
            <HeroContent {...heroState} />
        </StandaloneWrapper>
    );
};

export default Hero;
